import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import logo from '../../logo.png';
import { Flag, Search, CheckCircle, Clock, Truck, XCircle, Plus, X, ArrowLeft } from 'lucide-react';

export default function Movilizacion() {
  const navigate = useNavigate();
  const [movilizacion, setMovilizacion] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroSeccion, setFiltroSeccion] = useState('');
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const [formRegistro, setFormRegistro] = useState({
    persona_id: '',
    seccion_id: '',
    estatus_jornada: 'pendiente',
    observaciones: ''
  });

  useEffect(() => {
    fetchData();
    const intervalo = setInterval(fetchData, 30000);
    return () => clearInterval(intervalo);
  }, []);

  const fetchData = async () => {
    try {
      const [movRes, resRes, perRes] = await Promise.all([
        api.get('/movilizacion'),
        api.get('/movilizacion/resumen'),
        api.get('/capturas/personas')
      ]);
      setMovilizacion(movRes.data.movilizacion || []);
      setResumen(resRes.data);
      setPersonas(perRes.data.personas || []);

      // Secciones: usar las que vienen del resumen (ya filtradas por jerarquía)
      // Si por_seccion tiene datos, usarlas. Si no, pedir el catálogo filtrado
      const seccionesResumen = (resRes.data.por_seccion || []);
      if (seccionesResumen.length > 0) {
        setSecciones(seccionesResumen.map(s => ({
          id: s.id,
          numero_seccion: s.numero_seccion,
          municipio: s.municipio
        })));
      } else {
        // Sin datos de movilización aún — pedir secciones donde el usuario ha capturado
        const catRes = await api.get('/movilizacion/mis-secciones');
        setSecciones(catRes.data.secciones || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrar = async (e) => {
    e.preventDefault();
    try {
      await api.post('/movilizacion/registrar', formRegistro);
      setMensaje({ tipo: 'exito', texto: 'Persona registrada en jornada.' });
      fetchData();
      setTimeout(() => {
        setMostrarModal(false);
        setMensaje(null);
        setFormRegistro({ persona_id: '', seccion_id: '', estatus_jornada: 'pendiente', observaciones: '' });
      }, 1500);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al registrar.' });
    }
  };

  const handleActualizarEstatus = async (id, nuevoEstatus) => {
    try {
      await api.put(`/movilizacion/${id}/estatus`, { estatus_jornada: nuevoEstatus });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case 'voto': return 'bg-green-100 text-green-800';
      case 'pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'traslado': return 'bg-blue-100 text-blue-800';
      case 'no_localizado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstatusIcono = (estatus) => {
    switch (estatus) {
      case 'voto': return <CheckCircle size={14} className="text-green-600" />;
      case 'pendiente': return <Clock size={14} className="text-yellow-600" />;
      case 'traslado': return <Truck size={14} className="text-blue-600" />;
      case 'no_localizado': return <XCircle size={14} className="text-red-600" />;
      default: return <Clock size={14} />;
    }
  };

  const movilizacionFiltrada = movilizacion.filter(m => {
    const coincideBusqueda = `${m.nombre} ${m.apellido_paterno} ${m.telefono_principal}`
      .toLowerCase().includes(busqueda.toLowerCase());
    const coincideEstatus = filtroEstatus ? m.estatus_jornada === filtroEstatus : true;
    const coincideSeccion = filtroSeccion ? m.seccion_id?.toString() === filtroSeccion : true;
    return coincideBusqueda && coincideEstatus && coincideSeccion;
  });

  const personasNoRegistradas = personas.filter(p =>
    !movilizacion.find(m => m.persona_id === p.id)
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Cargando movilización...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">

      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={() => navigate('/dashboard')} title="Volver al Dashboard"
          className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-lg transition">
          <ArrowLeft size={20} />
        </button>
      </header>

      <main className="p-6 max-w-7xl mx-auto">

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Flag className="text-green-600" size={28} />
            Movilización Electoral — Día D
          </h2>
          <p className="text-gray-500 text-sm">Control en tiempo real del día de la elección</p>
        </div>

        {/* TARJETAS RESUMEN */}
        {resumen && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-gray-400">
              <p className="text-xs text-gray-500">Total Promovidos</p>
              <p className="text-2xl font-bold text-gray-800">{resumen.total_promovidos?.total || 0}</p>
            </div>
            {[
              { estatus: 'voto', label: '✅ Votaron', color: 'border-green-500' },
              { estatus: 'pendiente', label: '⏳ Pendientes', color: 'border-yellow-500' },
              { estatus: 'traslado', label: '🚗 En Traslado', color: 'border-blue-500' },
              { estatus: 'no_localizado', label: '❌ No Localizado', color: 'border-red-500' }
            ].map((item, i) => {
              const dato = resumen.por_estatus?.find(e => e.estatus_jornada === item.estatus);
              return (
                <div key={i} className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${item.color} cursor-pointer hover:shadow-md transition`}
                  onClick={() => setFiltroEstatus(filtroEstatus === item.estatus ? '' : item.estatus)}>
                  <p className="text-xs text-gray-500">{item.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{dato?.total || 0}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* AVANCE POR SECCIÓN — siempre visible, aunque todo esté en ceros */}
        {secciones.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">📍 Avance por Sección</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {secciones.map((s, i) => {
                // Buscar datos de movilización para esta sección
                const datos = resumen?.por_seccion?.find(
                  ps => ps.numero_seccion === s.numero_seccion
                ) || {};
                // Sobreescribir s con datos combinados
                s = { ...s, ...datos };
                const pct = s.meta_votos > 0
                  ? Math.round((s.votaron / s.meta_votos) * 100)
                  : 0;
                return (
                  <div key={i} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-gray-800">Sección {s.numero_seccion}</span>
                      <span className="text-xs text-gray-400">{s.municipio}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Avance</span>
                      <span className="font-semibold text-green-600">{pct}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                      <div className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <div className="grid grid-cols-4 gap-1 text-center text-xs">
                      <div className="bg-gray-50 rounded p-1">
                        <p className="font-bold text-gray-700">{s.registrados || 0}</p>
                        <p className="text-gray-400">Reg.</p>
                      </div>
                      <div className="bg-green-50 rounded p-1">
                        <p className="font-bold text-green-700">{s.votaron || 0}</p>
                        <p className="text-green-400">Votos</p>
                      </div>
                      <div className="bg-yellow-50 rounded p-1">
                        <p className="font-bold text-yellow-700">{s.pendientes || 0}</p>
                        <p className="text-yellow-400">Pend.</p>
                      </div>
                      <div className="bg-red-50 rounded p-1">
                        <p className="font-bold text-red-700">{s.no_localizados || 0}</p>
                        <p className="text-red-400">N/L</p>
                      </div>
                    </div>
                    {s.meta_votos > 0 && (
                      <p className="text-xs text-gray-400 mt-2 text-center">
                        Meta: {s.meta_votos} votos
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* FILTROS Y TABLA */}
        <div className="flex flex-wrap gap-3 mb-4 items-center justify-between">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar persona..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <select value={filtroSeccion} onChange={(e) => setFiltroSeccion(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="">Todas las secciones</option>
              {secciones.map((s, i) => (
                <option key={s.id || i} value={s.id}>{s.numero_seccion} - {s.municipio}</option>
              ))}
            </select>
            {filtroEstatus && (
              <button onClick={() => setFiltroEstatus('')}
                className="px-3 py-2 bg-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-300 transition">
                ✕ Quitar filtro
              </button>
            )}
          </div>
          <button onClick={() => setMostrarModal(true)}
            className="text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            <Plus size={18} /> Registrar Persona
          </button>
        </div>

        {/* TABLA */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">
              Mostrando {movilizacionFiltrada.length} de {movilizacion.length} registros
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Nombre</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Teléfono</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Sección</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Estatus</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Actualizar</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Hora</th>
              </tr>
            </thead>
            <tbody>
              {movilizacionFiltrada.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-400">
                    No hay registros de movilización
                  </td>
                </tr>
              ) : movilizacionFiltrada.map((m) => (
                <tr key={m.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">{m.nombre} {m.apellido_paterno}</td>
                  <td className="py-3 px-4">{m.telefono_principal}</td>
                  <td className="py-3 px-4">{m.numero_seccion || '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium w-fit ${getEstatusColor(m.estatus_jornada)}`}>
                      {getEstatusIcono(m.estatus_jornada)}
                      {m.estatus_jornada?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1">
                      {[
                        { key: 'voto', label: '✅', title: 'Votó' },
                        { key: 'traslado', label: '🚗', title: 'En traslado' },
                        { key: 'no_localizado', label: '❌', title: 'No localizado' },
                        { key: 'pendiente', label: '⏳', title: 'Pendiente' }
                      ].map(btn => (
                        <button key={btn.key}
                          onClick={() => handleActualizarEstatus(m.id, btn.key)}
                          title={btn.title}
                          className={`px-2 py-1 rounded text-xs font-medium transition ${
                            m.estatus_jornada === btn.key
                              ? 'bg-gray-800 text-white'
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}>
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-400">
                    {new Date(m.hora_actualizacion).toLocaleTimeString('es-MX')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          🔄 Actualización automática cada 30s &nbsp;·&nbsp; DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {/* MODAL REGISTRAR */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-lg">🗳️ Registrar en Jornada</h3>
              <button onClick={() => { setMostrarModal(false); setMensaje(null); }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {mensaje && (
                <div className={`p-3 rounded-lg text-sm ${
                  mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
                }`}>{mensaje.texto}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Persona *</label>
                <select value={formRegistro.persona_id} onChange={(e) => setFormRegistro({...formRegistro, persona_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                  <option value="">Seleccionar...</option>
                  {personasNoRegistradas.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre} {p.apellido_paterno} — {p.telefono_principal}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sección *</label>
                <select value={formRegistro.seccion_id} onChange={(e) => setFormRegistro({...formRegistro, seccion_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                  <option value="">Seleccionar...</option>
                  {secciones.map((s, i) => (
                    <option key={s.id || i} value={s.id}>{s.numero_seccion} - {s.municipio}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estatus Inicial</label>
                <select value={formRegistro.estatus_jornada} onChange={(e) => setFormRegistro({...formRegistro, estatus_jornada: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="pendiente">⏳ Pendiente</option>
                  <option value="traslado">🚗 En Traslado</option>
                  <option value="voto">✅ Ya Votó</option>
                  <option value="no_localizado">❌ No Localizado</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea value={formRegistro.observaciones} onChange={(e) => setFormRegistro({...formRegistro, observaciones: e.target.value})}
                  rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setMostrarModal(false); setMensaje(null); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                  Cancelar
                </button>
                <button onClick={handleRegistrar}
                  className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                  style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                  Registrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
